(function (global, factory) {
  'use strict';

  if (typeof define === 'function' && define.amd) {
    // If supports for AMD is available return the value to the AMD Loader
    // and to the global (window) object in case any JS module is expecting
    // the SearchBarHelper to exists on the window object.
    define('searchBarHelper',[], () => {
      global.SearchBarHelper = factory(global);
      return global.SearchBarHelper;
    });
  } else if (typeof exports === 'object') {
    // If supports for Common JS is available export the module
    // and pass its value to the global (window) object in case non Common JS code
    // is expected to work with this SearchBarHelper Module.
    const handler = factory(global);
    global.SearchBarHelper = handler;
    module.exports = handler;
  } else {
    // If not support for AMD or Common JS is available register the module in the global scope.
    global.SearchBarHelper = factory(global);
  }
}(typeof window !== 'undefined' ? window : {}, (global) => {
  'use strict';

  global.NewHomeSource = global.NewHomeSource || {};

  /**
   * Search result category types.
   * @enum {number}
   * Category type for 'articles' as defined by the backend/API(2.0) contract (e.g., Article = 5)
   */
  const searchResultsCategoryType = {
    articles: 5
  };

  const searchBarCompletionSuggestionTypes = {
    StateEquivalent: 'state',
    City: 'city',
    CountyEquivalent: 'county',
    Neighborhood: 'neighborhood',
    PostalCode: 'zip',
    SchoolDistrict: 'schoolDistrict',
    MarketAreaPopularlyNamedRegion: 'marketArea',
    Article: 'article',
    Community: 'community',
    Builder: 'builder',
    MasterPlannedCommunity: 'mpc',
  };

  /**
   * Creates a keyboard navigator for a list of items.
   * Supports ArrowUp, ArrowDown, Home, End, and Enter keys.
   */
  function createListNavigator(container, {
    getItems,
    onEnter,
    activeClass = 'active'
  }) {
    let currentIndex = -1;
    let clickHandlers = [];

    const list = () => getItems() || [];

    const clearClickHandlers = () => {
      clickHandlers.forEach(({ item, handler }) => item.removeEventListener('click', handler));
      clickHandlers = [];
    };

    const setActive = (index) => {
      const items = list();
      if (!items.length) {
        return;
      }

      const newIndex = (index + items.length) % items.length;
      if (newIndex === currentIndex) {
        return;
      }

      // Remove active class from all
      items.forEach((item) => item.classList.remove(activeClass));

      // Update current
      currentIndex = newIndex;
      const item = items[currentIndex];

      // Apply active class
      if (item) {
        item.classList.add(activeClass);
        item.scrollIntoView({ block: 'nearest' });
      }

      // Reset old click handlers and attach new one
      clearClickHandlers();
      if (item && onEnter) {
        const handler = () => onEnter(item, currentIndex);
        item.addEventListener('click', handler);
        clickHandlers.push({ item, handler });
      }
    };

    const handleEnter = () => {
      const items = list();
      if (currentIndex >= 0 && currentIndex < items.length && onEnter) {
        onEnter(items[currentIndex], currentIndex);
      }
    };

    const keyActions = {
      ArrowDown: () => setActive(currentIndex + 1),
      ArrowUp: () => setActive(currentIndex - 1),
      Enter: handleEnter,
      Home: () => setActive(0),
      End: () => setActive(list().length - 1),
    };

    const keyListener = (event) => {
      const action = keyActions[event.key];
      if (!action) {
        return;
      }
      event.preventDefault();
      action();
    };

    container.addEventListener('keydown', keyListener);
    const attachClickHandlers = () => {
      list().forEach((item, index) => {
        const handler = () => {
          currentIndex = index;
          if (onEnter) {
            onEnter(item, index);
          }
          setActive(index);
        };
        item.addEventListener('click', handler);
        clickHandlers.push({ item, handler });
      });
    };

    attachClickHandlers();

    return {
      destroy: () => {
        container.removeEventListener('keydown', keyListener);
        clearClickHandlers();
      },
      setActive,
      reset: () => setActive(-1),
      getIndex: () => currentIndex,
    };
  }

  /**
   * Returns the HTML template for a single search result item.
   * Placeholders like {{TITLE}}, {{DESCRIPTION}}, etc., will be dynamically replaced at runtime.
   *
   * @returns {string} The HTML string for a search result item.
   */
  function getSearchResultTemplate() {
    const template = `
        <div class="nhs-n1-c-search-bar-results__item" data-search-bar-result-item
          data-ga4-event-click_target="{{CLICKTARGET}}"
          data-ga4-event-click_target2="{{CLICKTARGET2}}">
          <div class="nhs-n1-c-search-bar-results__item__container">
            <div
              class="nhs-n1-c-search-bar-results__item__title"
              data-location-url="{{LOCATIONURL}}"
              data-location-name="{{LOCATIONNAME}}"
              data-location-type="{{LOCATIONTYPE}}"
              data-market-area-name="{{MARKETAREANAME}}"
              data-market-area-id="{{MARKETAREAID}}"
              data-state-equivalent-abbreviation="{{STATEEQUIVALENTABBREVIATION}}"
              data-county="{{COUNTY}}"
              data-zip-code="{{ZIPCODE}}"
              data-city="{{CITY}}"
              data-school-district="{{SCHOOLDISTRICT}}"
              data-market-state="{{MARKETSTATE}}"
              data-should-open-in-new-tab="{{NEWTAB}}"
              data-title="{{TITLE_PLAIN}}">
              {{TITLE}}
            </div>
            <div class="nhs-n1-c-search-bar-results__item__container__description">
              {{DESCRIPTION}}
            </div>
          </div>
        </div>
      `;
    return template;
  }

  /**
   * Wraps matched text in a span with a specified CSS class.
   * @param {string} text - The text to search within.
   * @param {RegExp} regex - The regular expression to match text.
   * @param {string} cssClass - The CSS class to apply to matched text.
   * @returns {string} The text with matched segments wrapped in a span.
   */
  function wrapMatches(text, regex, cssClass) {
    return text.replace(regex, (match) => `<span class="${cssClass}">${match}</span>`);
  }

  /**
   * Escapes special characters in a string for use in a regular expression.
   * @param {string} text - The text to escape.
   * @returns {string} The escaped text.
   */
  function escapeRegexChars(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /**
   * Highlights occurrences of searchText in title by wrapping them in a span with a specific CSS class.
   * @param {string} title - The text in which to highlight matches.
   * @param {string} searchText - The text to search for and highlight.
   * @returns {string} The title with highlighted matches.
   */
  function highlightMatch(title, searchText) {
    const cssRegularText = 'nhs-n1-c-search-bar-results__item__container__title--regular';
    const cssBoldText = 'nhs-n1-c-search-bar-results__item__container__title--bold';

    if (!searchText || !title || searchText.trim().length <= 1) {
      return `<div class="${cssRegularText}">${title || ''}</div>`;
    }

    const escapedSearch = escapeRegexChars(searchText);
    const searchRegex = new RegExp(escapedSearch, 'gi');
    const highlightedText = wrapMatches(title, searchRegex, cssBoldText);

    return `<div class="${cssRegularText}">${highlightedText}</div>`;
  }

  /**
   * Truncates text to a specified maximum length and appends an ellipsis if truncated.
   * @param {string} text - The text to truncate.
   * @param {number} maxLength - The maximum length of the text before truncation.
   * @returns {string} The truncated text with an ellipsis if applicable.
   */
  function truncateTextWithEllipsis(text, maxLength = 90) {
    if (text.length > maxLength) {
      return text.slice(0, maxLength).trim() + '...';
    }
    return text;
  }

  /**
   * Checks if the provided data is a non-empty array.
   * @param {Array} data - The data to check.
   * @returns {boolean} True if the data is a non-empty array, false otherwise.
   */
  function hasListItems(data) {
    return Array.isArray(data) && data.length > 0;
  }

  /**
   * Determines the text limit based on device type.
   * @param {boolean} isMobile - Indicates if the device is mobile.
   * @returns {number} The text limit for the device type.
   */
  function getTextLimit(isMobile) {
    const textLimitMobile = 45;
    const textLimitDesktop = 83;
    return isMobile ? textLimitMobile : textLimitDesktop;
  }

  /**
   * Determines if a search result should open in a new tab based on its category type.
   * @param {Object} item - The search result item.
   * @returns {boolean} True if the item should open in a new tab, false otherwise.
   */
  function shouldOpenSearchResultInNewTab(item) {
    return item && item.SearchBarResultCategoryType === searchResultsCategoryType.articles;
  }

  /**
   * Generates HTML for a single search result item.
   * @param {Object} item - The search result item.
   * @param {string} item.DisplayText - The display text of the item.
   * @param {string} item.LocationUrl - The URL of the item.
   * @param {string} item.DisplayDescription - The description of the item.
   * @param {number} textLimit - The maximum length of the display text.
   * @param {string} searchText - The search text used for highlighting.
   * @returns {string} The generated HTML for the search result item.
   */
  function generateSingleResultHTML(item, textLimit, searchText) {
    const truncatedText = truncateTextWithEllipsis(item.DisplayText, textLimit);
    const title = highlightMatch(truncatedText, searchText);
    const titlePlain = truncatedText;
    return getSearchResultTemplate()
      .replace('{{TITLE}}', title)
      .replace('{{TITLE_PLAIN}}', titlePlain)
      .replace('{{LOCATIONURL}}', item.LocationUrl || '#')
      .replace('{{NEWTAB}}', shouldOpenSearchResultInNewTab(item))
      .replace('{{DESCRIPTION}}', item.DisplayDescription)
      .replace('{{CLICKTARGET}}', 'SearchBarResult')
      .replace('{{CLICKTARGET2}}', searchBarCompletionSuggestionTypes[item.CompletionSuggestionType])
      .replace('{{LOCATIONNAME}}', item.Name || '')
      .replace('{{LOCATIONTYPE}}', item.CompletionSuggestionType || '')
      .replace('{{MARKETAREANAME}}', item.MarketAreaName || '')
      .replace('{{MARKETAREAID}}', item.MarketAreaId || '')
      .replace('{{STATEEQUIVALENTABBREVIATION}}', item.StateEquivalentAbbreviation || '')
      .replace('{{COUNTY}}', item.Name || '')
      .replace('{{ZIPCODE}}', item.PostalCode || '')
      .replace('{{CITY}}', item.CityName || '')
      .replace('{{SCHOOLDISTRICT}}', item.SchoolDistrict || '')
      .replace('{{MARKETSTATE}}', item.MarketPreferredStateEquivalentAbbreviation || '');
  }

  /**
   * Sets the visibility of the main header.
   * @param {boolean} shouldHide - Whether to hide the header.
   */
  function setHeaderVisibility(shouldHide, headerParam = null, detailsContainer = null) {
    const header = headerParam;
    if (header && detailsContainer === null) {
      header.hidden = shouldHide;
    }
  }

  /**
   * Sets the visibility of the side menu.
   * @param {boolean} shouldHide - Whether to hide the side menu.
   */
  function setSideMenuVisibility(shouldHide, sideMenuParam = null) {
    const sideMenu = sideMenuParam;
    if (sideMenu) {
      sideMenu.hidden = shouldHide;
    }
  }

  /**
   * Loads the CSS for the search bar modal if not already loaded.
   */
  function updateHomeSearchBarVisibility(homeSearchBar, shouldOpen) {
    if (homeSearchBar) {
      homeSearchBar.classList.toggle('nhs-c-search-box', !shouldOpen);
    }
  }

  /**
   * Loads the CSS for the search bar modal if not already loaded.
   */
  function updateModalLayout(homeSearchBar, detailsContainer, modal, loadModalCss) {
    if (!(homeSearchBar || detailsContainer) || !modal) {
      loadModalCss();
      return;
    }

    modal.classList.add('nhs-n1-c-search-bar-modal--row');
  }

  /**
   * Loads the CSS for the search bar modal.
   */
  function updateLayoutVisibility(shouldOpen, config) {
    if (!config || Object.keys(config).length === 0) {
      return;
    }

    if (!config.isBasicDetailsPage) {
      setHeaderVisibility(shouldOpen, config.header, config.detailsContainer);
    }

    setSideMenuVisibility(shouldOpen, config.sideMenu);
    updateHomeSearchBarVisibility(config.homeSearchBar, shouldOpen);
    updateModalLayout(config.homeSearchBar, config.detailsContainer, config.modal, config.loadModalCss);
  }

  /**
   * Capitalizes the first letter of a string.
   * @param {string} str - The string to capitalize.
   * @returns {string} The capitalized string.
   */
  function capitalizeFirstLetter(str) {
    if (str) {
      return str.charAt(0).toUpperCase() + str.slice(1);
    }
    return '';
  }

  /**
   * Normalizes spaces in a string by replacing multiple spaces with a single space and trimming.
   * @param {string} text - The text to normalize.
   * @returns {string} The normalized text.
   */
  function normalizeSpaces(text) {
    if (!text) {
      return '';
    }

    return text.replace(/\s+/g, ' ').trim();
  }

  /**
   * Enumeration for completion suggestion types.
   */
  const completionSuggestionType = {
    neighborhood: 'Neighborhood',
    countyEquivalent: 'CountyEquivalent',
    postalCode: 'PostalCode',
    city: 'City',
    schoolDistrict: 'SchoolDistrict',
    marketAreaPopularlyNamedRegion: 'MarketAreaPopularlyNamedRegion'
  };

  /**
   * Formats the search text based on the location type.
   * @param {Object} location - The location object.
   * @returns {string} The formatted search text.
   */
  function getFormattedSearchText(location) {
    if (!location) {
      return '';
    }

    const locationType = location.locationType;

    let formattedSearchText = '';

    switch (locationType) {
      case completionSuggestionType.neighborhood: {
        formattedSearchText = [
          `${location.locationName} ${capitalizeFirstLetter(locationType)}`,
          capitalizeFirstLetter(location.marketAreaName),
          capitalizeFirstLetter(location.stateEquivalentAbbreviation)
        ].join(', ');
        break;
      }
      case completionSuggestionType.countyEquivalent: {
        formattedSearchText = `${location.county} County, ${location.stateEquivalentAbbreviation}`;
        break;
      }
      case completionSuggestionType.postalCode: {
        formattedSearchText = `${location.postalCode || ''}`;
        break;
      }
      case completionSuggestionType.city: {
        formattedSearchText = `${location.city}, ${location.stateEquivalentAbbreviation}`;
        break;
      }
      case completionSuggestionType.schoolDistrict: {
        formattedSearchText = `${location.locationName || ''}`;
        break;
      }
      case completionSuggestionType.marketAreaPopularlyNamedRegion: {
        const stateAbbreviation = location.marketState || location.stateEquivalentAbbreviation || '';
        formattedSearchText = `${location.marketAreaName}, ${stateAbbreviation} Area`;
        break;
      }
      default: {
        formattedSearchText = '';
        break;
      }
    }

    const normalizedText = normalizeSpaces(formattedSearchText);
    return normalizedText;
  }

  /**
   * Formats location parameters for a query string.
   * @param {Object} data - The location data.
   * @returns {string} The formatted location parameters.
   */
  function getFormattedLocationParams(data) {
    const market = data.marketAreaId;
    const state = data.marketState;
    const searchText = getFormattedSearchText(data);
    const urlEncodedText = encodeURIComponent(searchText);

    return `market=${market}&state=${state}&searchtext=${urlEncodedText}`;
  }

  /**
   * Updates the 'Personal_1' cookie with the last search text.
   * @param {string} searchText - The last search text to store in the cookie.
   */
  function updateLastSearchCookie(searchText) {
    global.NewHomeSource.Modules.cookieHandler.createCookie('Personal', searchText, '', true);
  }

  /**
   * Updates the position of search results container if panning out of left or right.
   * @param {HTMLElement} resultsContainerParam - The search results container element to reposition if necessary.
   */
  function handleResultsContainerPlacement(resultsContainerParam) {
    const resultsContainer = resultsContainerParam;

    if (handleResultsContainerPlacement.pending) {
      return;
    }
    handleResultsContainerPlacement.pending = true;

    requestAnimationFrame(() => {
      // Perform the work after layout from prior writes has been applied
      requestAnimationFrame(() => {
        handleResultsContainerPlacement.pending = false;
        const documentWidth = document.documentElement.clientWidth;
        const resultsContainerRect = resultsContainer.getBoundingClientRect();
        let reposition = 0;
        if (resultsContainerRect.right > documentWidth) {
          reposition -= (resultsContainerRect.right - documentWidth);
        }
        if (resultsContainerRect.left < 0) {
          reposition += -resultsContainerRect.left;
        }
        if (reposition !== 0) {
          const match = (resultsContainer.style.transform || '').match(/translateX\((-?\d+(?:\.\d+)?)px\)/);
          const existing = match ? parseFloat(match[1]) : 0;
          resultsContainer.style.transform = `translateX(${existing + reposition}px)`;
        }
      });
    });
  }

  /**
   * Toggles the scroll behavior of an element by adding or removing the 'nhs-u-overflow--hidden' class.
   * @param {HTMLElement} element - The element to toggle scroll behavior on.
   * @param {boolean} disable - Whether to disable or enable scrolling.
   */
  function toggleScroll(element, disable) {
    if (!element || !element.classList) {
      return;
    }

    element.classList.toggle('nhs-u-overflow--hidden', disable);
  }

  return {
    createListNavigator,
    hasListItems,
    escapeRegexChars,
    getSearchResultTemplate,
    wrapMatches,
    highlightMatch,
    truncateTextWithEllipsis,
    getTextLimit,
    generateSingleResultHTML,
    updateLayoutVisibility,
    getFormattedLocationParams,
    getFormattedSearchText,
    updateLastSearchCookie,
    completionSuggestionType,
    handleResultsContainerPlacement,
    toggleScroll
  };
}));

